import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@amzn/awsui-components-core/header';
import Container from '@amzn/awsui-components-core/container';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import PropertyFilter from '@amzn/awsui-components-core/property-filter';
import PercentageMetricCard from './PercentageMetricCard';
import VolumeCard from './VolumeCard';
import DataModeSelector from './DataModeSelector';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { dataModeChanged, selectedWeekChanged, filterQueryChanged } from 'src/features/dashboard/dashboardSlice';
import { FILTER_PROPERTIES } from 'src/config/filterConfig';
import { useFilterOptions } from 'src/components/filters/useFilterOptions';
import { useMetricsWindow } from 'src/features/api/useMetricsWindow';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import { homeWindow, buildDurationWindow, weekBefore } from 'src/features/api/durationRange';
import { DataMode } from 'src/types/metricsApi.types';
import { selectWbrDashboardData } from 'src/features/api/metricsSelectors';
import { buildDcrPredicate, selectedMetricGroups } from 'src/features/api/filterPredicate';
import { WBR_DASHBOARD_ID, WBR_REVIEW_TYPE } from 'src/constants';

const WbrMetricsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const mode = useAppSelector((s) => s.dashboard.dataMode);
  const selectedWeek = useAppSelector((s) => s.dashboard.selectedWeek);
  const filterQuery = useAppSelector((s) => s.dashboard.filterQuery);
  // Read only to warm the detail page's window (see detailWeeks below); this
  // page's own window is always the default 5.
  const reportingWeek = useAppSelector((s) => s.dashboard.reportingWeek);
  const lastXWeeks = useAppSelector((s) => s.dashboard.lastXWeeks);
  const forwardXWeeks = useAppSelector((s) => s.dashboard.forwardXWeeks);
  // Data-driven, cascading dropdown values (QuickSight parity) — same source
  // as the FilterBar on the detail pages.
  const filteringOptions = useFilterOptions();

  // The metric catalog (names, goals, groups, operand identity) — one fetch
  // per session, in PARALLEL with the metrics; joined by metricKey below.
  const { data: definitionsResponse } = useGetMetricDefinitionsQuery({
    dashboardId: WBR_DASHBOARD_ID,
    reviewType: WBR_REVIEW_TYPE,
  });

  // Week identity comes from the service (it owns the reporting calendar,
  // including year boundaries), so naming this page's weeks depends on the
  // universe. One small GET, cached for the session.
  const { data: weekData } = useGetWeekNumbersQuery();

  // Weeks are cached individually, so overlapping windows across pages are
  // ordinary cache hits and shifting a window fetches only the weeks it gains
  // (spec metrics-window-caching R1). The neighbours of this window are warmed
  // once it resolves, making a nearby week switch instant (R5).
  const isSnapshot = mode === 'snapshot';
  const weeks = useMemo(
    () => (weekData ? homeWindow(weekData.weeks, weekData.currentWeek, isSnapshot) : undefined),
    [weekData, isSnapshot],
  );
  // The same page in the other mode, warmed so the Live/Snapshot toggle does
  // not reload. The two windows differ: a snapshot ends at the last completed
  // week, because the running week has no capture.
  const otherModeWeeks = useMemo(
    () => (weekData ? homeWindow(weekData.weeks, weekData.currentWeek, !isSnapshot) : undefined),
    [weekData, isSnapshot],
  );
  // The detail page's window, warmed because "View details" is the primary
  // action here. Built from the same controls the detail page reads, so the
  // cache keys match exactly.
  const detailWeeks = useMemo(() => {
    if (!weekData) return undefined;
    const anchor =
      reportingWeek ??
      (isSnapshot ? weekBefore(weekData.weeks, weekData.currentWeek) : weekData.currentWeek);
    return buildDurationWindow(
      weekData.weeks,
      weekData.currentWeek,
      anchor,
      lastXWeeks,
      isSnapshot ? 0 : forwardXWeeks,
    )?.durationRange;
  }, [weekData, reportingWeek, lastXWeeks, forwardXWeeks, isSnapshot]);

  // Priority order: the mode toggle first (one click, whole page), then the
  // page this one links to. No neighbour weeks — this window is fixed at the
  // default 4 weeks back, so a week outside it is unreachable from here. The
  // detail page owns the week control and warms its own neighbours.
  const alsoWarm = useMemo(() => {
    const targets: Array<{ dataMode: DataMode; weeks: string[] }> = [];
    if (otherModeWeeks) {
      targets.push({ dataMode: isSnapshot ? 'LIVE' : 'SNAPSHOT', weeks: otherModeWeeks });
    }
    if (detailWeeks) {
      targets.push({ dataMode: isSnapshot ? 'SNAPSHOT' : 'LIVE', weeks: detailWeeks });
    }
    return targets;
  }, [otherModeWeeks, detailWeeks, isSnapshot]);

  const {
    data: apiResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useMetricsWindow({
    id: WBR_DASHBOARD_ID,
    dataMode: isSnapshot ? 'SNAPSHOT' : 'LIVE',
    weeks,
    alsoWarm,
  });

  const dashboardData = useMemo(
    () =>
      selectWbrDashboardData(
        apiResponse,
        definitionsResponse?.definitions,
        buildDcrPredicate(filterQuery),
        selectedMetricGroups(filterQuery),
      ),
    [apiResponse, definitionsResponse, filterQuery],
  );

  // The FIRST (oldest) week of the window exists ONLY for delta comparison
  // and is excluded from the dropdown — unless it is the only week loaded
  // (sparse data / single-week snapshot), in which case it is selectable
  // and deltas simply show no change.
  const selectableWeeks = useMemo(() => {
    if (!dashboardData) return [];
    return dashboardData.weeks.length > 1 ? dashboardData.weeks.slice(1) : dashboardData.weeks;
  }, [dashboardData]);

  const weekIndex = dashboardData && selectedWeek ? dashboardData.weeks.indexOf(selectedWeek) : -1;
  const resolvedIndex = weekIndex >= 1 ? weekIndex : (dashboardData ? dashboardData.weeks.length - 1 : 0);

  // Sync Redux if selected week isn't in the selectable range
  const needsSync = Boolean(dashboardData && dashboardData.weeks.length > 1 && (weekIndex < 1 || !selectedWeek));
  const latestWeek = dashboardData?.weeks[dashboardData.weeks.length - 1] ?? null;
  React.useEffect(() => {
    if (needsSync && latestWeek) {
      dispatch(selectedWeekChanged(latestWeek));
    }
  }, [needsSync, latestWeek, dispatch]);

  // The page shell (header, mode/week controls, filter bar) renders in EVERY
  // state — only the content area below it switches between spinner, error,
  // empty and cards. The controls are how the user escapes an empty state
  // (e.g. snapshot has no captures but live has data), so an empty state that
  // hides them is a trap.
  const content = (() => {
    if (error) {
      return (
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <SpaceBetween size="s" direction="vertical" alignItems="center">
            <Box color="text-status-error" fontWeight="bold">Failed to load metrics</Box>
            <Button onClick={refetch}>Retry</Button>
          </SpaceBetween>
        </Box>
      );
    }
    if (isLoading) {
      return (
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <Spinner size="large" />
        </Box>
      );
    }
    // Loaded but no rows (e.g. backend cache not yet populated for this
    // dashboard/date range) — an explicit empty state, NOT the spinner.
    if (!dashboardData) {
      return (
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <SpaceBetween size="s" direction="vertical" alignItems="center">
            <Box fontWeight="bold">No metrics data available</Box>
            <Box color="text-body-secondary">
              The backend returned no rows for this dashboard in {mode} mode for this date range.
            </Box>
            <Button onClick={refetch}>Refresh</Button>
          </SpaceBetween>
        </Box>
      );
    }
    return (
      <>
        {/* Top: all percentage metrics (generic — any % metric from API lands here) */}
        {dashboardData.percentageMetrics.length > 0 && (
          <Container header={<Header variant="h2">Delivery Performance</Header>}>
            <div className="otd-card-row">
              {dashboardData.percentageMetrics.map((m) => (
                <PercentageMetricCard
                  key={m.metricName}
                  metricName={m.metricName}
                  owner={m.owner}
                  goal={m.goal}
                  weeklyValues={m.weeklyValues}
                  weekIndex={resolvedIndex}
                  weeks={dashboardData.weeks}
                />
              ))}
            </div>
          </Container>
        )}

        {/* Bottom: all volume/count metrics (generic — any volume metric lands here) */}
        {dashboardData.volumeMetrics.length > 0 && (
          <Container header={<Header variant="h2">Volume Metrics</Header>}>
            <div className="volume-card-grid">
              {dashboardData.volumeMetrics.map((m) => (
                <VolumeCard
                  key={m.metricName}
                  metricName={m.metricName}
                  owner={m.owner}
                  goal={m.goal}
                  weeklyVolumes={m.weeklyVolumes}
                  weeklyAttainment={m.weeklyAttainment}
                  weekIndex={resolvedIndex}
                  weeks={dashboardData.weeks}
                />
              ))}
            </div>
          </Container>
        )}
      </>
    );
  })();

  return (
    <div className="home-page">
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="s">
              <DataModeSelector
                mode={mode}
                selectedWeek={selectedWeek}
                availableWeeks={selectableWeeks}
                onModeChange={(m) => dispatch(dataModeChanged(m))}
                onWeekChange={(w) => dispatch(selectedWeekChanged(w))}
              />
              <Button variant="primary" onClick={() => navigate('/detail')}>View details</Button>
            </SpaceBetween>
          }
        >
          FAI - WBR Executive Summary
          {isFetching && !isLoading && <Spinner size="normal" />}
        </Header>

        <PropertyFilter
          query={filterQuery}
          onChange={({ detail }) => dispatch(filterQueryChanged(detail as typeof filterQuery))}
          filteringProperties={FILTER_PROPERTIES}
          filteringOptions={filteringOptions}
          disableFreeTextFiltering={true}
          i18nStrings={{
            filteringAriaLabel: 'Filter metrics',
            filteringPlaceholder: 'Search or select a filter',
            groupValuesText: 'Values',
            groupPropertiesText: 'Properties',
            operatorsText: 'Operators',
            operationAndText: 'and',
            operationOrText: 'or',
            clearFiltersText: 'Clear filters',
            removeTokenButtonAriaLabel: (token) => `Remove ${token.propertyKey} ${token.operator} ${token.value}`,
            enteredTextLabel: (text) => `Use: "${text}"`,
          }}
        />

        {content}
      </SpaceBetween>
    </div>
  );
};

export default WbrMetricsDashboard;
